import { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Workbook, type WorkbookInstance } from '@fortune-sheet/react';
import type { Sheet as FortuneSheet, CellWithRowAndCol } from '@fortune-sheet/core';
import '@fortune-sheet/react/dist/index.css';

import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';

/**
 * T-0036 / E04-F05 — Inline Excel rendering with cell-level annotation.
 *
 * --------------------------------------------------------------------------
 * SpreadJS substitution note
 * --------------------------------------------------------------------------
 * The original feature spec (ADR-014) picks SpreadJS for production. SpreadJS
 * is a commercial library; for local dev / OSS baselines we substitute
 * Fortune-Sheet (https://github.com/ruilisi/fortune-sheet) — an MIT-licensed
 * Excel-grade React component supporting:
 *   - multi-sheet workbooks (sheet tabs along the bottom)
 *   - cell formulas + formatting
 *   - cell-click + selection events
 *   - virtualized rendering of large grids
 *
 * The swap is roughly 90% feature-compatible for this v1 surface; the
 * surface we actually use is small (render + cell selection). When the
 * production cutover happens, the consumers of this component (route +
 * modal) are wired through `onCellClick` and `currentSelection` props so
 * a SpreadJS-backed version of `<ExcelWorkbook>` can drop in.
 *
 * --------------------------------------------------------------------------
 * Performance budget (T-0036 §6, feature spec §10/§12)
 * --------------------------------------------------------------------------
 *   - workbook render:        <3s for 50-sheet workbooks
 *   - cell-click interaction: <100ms (annotation modal open)
 *
 * Both budgets are inherited from Fortune-Sheet's native rendering: it
 * renders one active sheet at a time on a canvas (virtualized), so
 * a 50-sheet workbook is no more expensive to render than a single
 * sheet plus the cost of switching active sheets. Cell-click bubbles
 * through the `afterSelectionChange` hook synchronously — the modal
 * trigger is a `setState` call, well under the 100ms budget.
 *
 * No benchmark is run here; the ticket spec just asks for the budget to
 * be documented.
 */

export interface ExcelCellSelection {
  /** Sheet name as it appears in the workbook (e.g. "Defects-P1"). */
  sheet_name: string;
  /** A1-style cell reference (e.g. "B47"). */
  cell_a1: string;
  /** Best-effort string snapshot of the cell value at click time. */
  value_snapshot: string | null;
  /** 0-based row index — useful when the parent needs to highlight. */
  row: number;
  /** 0-based column index. */
  column: number;
}

export interface ExcelWorkbookProps {
  /**
   * The raw workbook bytes (ArrayBuffer). The component parses this via
   * SheetJS into Fortune-Sheet's sheet format on mount, then disposes
   * the parsed XLSX workbook.
   */
  bytes: ArrayBuffer;
  /** Called when the user selects a cell (single-cell selection only). */
  onCellClick?: (sel: ExcelCellSelection) => void;
}

/**
 * Convert a 0-based column index to a spreadsheet-style A1 letter sequence.
 * 0 → "A", 25 → "Z", 26 → "AA", 701 → "ZZ", etc. Pure function so it can
 * be unit-tested without DOM dependencies.
 */
export function columnIndexToA1(index: number): string {
  if (index < 0 || !Number.isInteger(index)) {
    throw new Error(`columnIndexToA1: invalid column index ${index}`);
  }
  let n = index;
  let s = '';
  while (true) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}

/**
 * Convert SheetJS's workbook representation into Fortune-Sheet's data
 * shape. Fortune-Sheet expects one entry per non-empty cell in `celldata`
 * (sparse) plus a `name` per sheet.
 *
 * v1 surface — we project cells as `m` (display string) + `v` (raw value
 * which Fortune-Sheet computes formats from). Formulas + styles are
 * preserved as best-effort: when the original xlsx had a formula we set
 * `f`; otherwise we omit it.
 */
export function convertXlsxToFortuneSheet(bytes: ArrayBuffer): FortuneSheet[] {
  // Formulas are shown, so keep them; skip what the viewer never uses
  // (rich-text HTML, VBA, extra package parts) since the file is untrusted.
  const workbook = XLSX.read(new Uint8Array(bytes), {
    type: 'array',
    cellFormula: true,
    cellHTML: false,
    bookVBA: false,
    bookDeps: false,
    bookFiles: false,
  });
  const out: FortuneSheet[] = [];
  for (const [order, sheetName] of workbook.SheetNames.entries()) {
    const ws = workbook.Sheets[sheetName];
    if (!ws) continue;
    const ref = ws['!ref'];
    if (!ref) {
      // Empty sheet — Fortune-Sheet still wants a tab for it so the
      // user can navigate to it. Use a small default grid.
      out.push({
        name: sheetName,
        order,
        celldata: [],
        row: 100,
        column: 26,
      });
      continue;
    }
    const range = XLSX.utils.decode_range(ref);
    const celldata: CellWithRowAndCol[] = [];
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (!cell) continue;
        const cellValue: {
          v?: string | number | boolean;
          m?: string;
          f?: string;
        } = {};
        if (cell.v !== undefined && cell.v !== null) {
          cellValue.v = cell.v as string | number | boolean;
        }
        if (cell.w !== undefined) {
          cellValue.m = String(cell.w);
        } else if (cell.v !== undefined && cell.v !== null) {
          cellValue.m = String(cell.v);
        }
        if (cell.f !== undefined) {
          cellValue.f = `=${cell.f}`;
        }
        // Only push cells with at least one meaningful field; pure-style
        // empty cells are skipped to keep the celldata sparse.
        if (cellValue.v !== undefined || cellValue.m !== undefined || cellValue.f !== undefined) {
          celldata.push({ r, c, v: cellValue });
        }
      }
    }
    // Reserve a little headroom above the data so the user can scroll
    // past the last row to add new entries. v1 viewer is read-only so
    // this is purely cosmetic.
    out.push({
      name: sheetName,
      order,
      celldata,
      row: Math.max(range.e.r + 20, 100),
      column: Math.max(range.e.c + 5, 26),
    });
  }
  if (out.length === 0) {
    // Defensive — every workbook should have at least one sheet, but
    // if SheetJS returns an empty list we surface an empty Sheet1.
    out.push({ name: 'Sheet1', order: 0, celldata: [], row: 100, column: 26 });
  }
  return out;
}

/**
 * Inline Excel viewer. Parses the bytes on mount, renders Fortune-Sheet
 * read-only (allowEdit=false), and surfaces cell selections through
 * `onCellClick`.
 *
 * Read-only — the platform never mutates the source workbook per
 * feature spec §14. Annotations layer on top via EvidenceLinks.
 */
export function ExcelWorkbook({ bytes, onCellClick }: ExcelWorkbookProps) {
  const ref = useRef<WorkbookInstance>(null);
  const [parseError, setParseError] = useState<unknown>(null);
  const [data, setData] = useState<FortuneSheet[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    try {
      const sheets = convertXlsxToFortuneSheet(bytes);
      if (!cancelled) setData(sheets);
    } catch (err) {
      if (!cancelled) {
        setParseError(err);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [bytes]);

  // Cache the most-recently-clicked cell so we can emit the snapshot
  // even after Fortune-Sheet's internal state has moved on. The
  // `afterSelectionChange` hook fires on every selection change; we
  // narrow to single-cell selections (the annotation surface only
  // supports per-cell, not per-range).
  const settings = useMemo(() => {
    if (!data) return null;
    return {
      data,
      allowEdit: false,
      showToolbar: false,
      showFormulaBar: true,
      showSheetTabs: true,
      hooks: {
        afterSelectionChange: (
          _sheetId: string,
          selection: { row: number[]; column: number[] },
        ) => {
          if (!onCellClick) return;
          // Single-cell selection only — `row` is [startRow, endRow]; we
          // treat startRow===endRow + startCol===endCol as the
          // single-cell case.
          const r0 = selection.row[0];
          const r1 = selection.row[1];
          const c0 = selection.column[0];
          const c1 = selection.column[1];
          if (r0 === undefined || c0 === undefined) return;
          if (r0 !== r1 || c0 !== c1) return;
          // Pull the active sheet from the WorkbookInstance to surface
          // its name back to the parent.
          const sheet = ref.current?.getSheet();
          if (!sheet) return;
          const cellLetter = columnIndexToA1(c0);
          const cellA1 = `${cellLetter}${r0 + 1}`;
          let valueSnapshot: string | null = null;
          try {
            const m = ref.current?.getCellValue(r0, c0, { type: 'm' });
            const v = ref.current?.getCellValue(r0, c0, { type: 'v' });
            const snapshot = m ?? v;
            if (snapshot !== undefined && snapshot !== null) {
              valueSnapshot = String(snapshot);
            }
          } catch {
            // getCellValue throws on out-of-range; treat as no snapshot.
            valueSnapshot = null;
          }
          onCellClick({
            sheet_name: sheet.name,
            cell_a1: cellA1,
            value_snapshot: valueSnapshot,
            row: r0,
            column: c0,
          });
        },
      },
    } as const;
  }, [data, onCellClick]);

  if (parseError) {
    return (
      <div className="space-y-2">
        <ErrorMessage error={parseError} />
        <p className="text-sm text-muted-foreground">
          The workbook couldn't be parsed for inline view. Use the download link to open it in
          Excel.
        </p>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner /> Parsing workbook…
      </div>
    );
  }

  return (
    <div className="h-[640px] w-full overflow-hidden rounded-md border">
      <Workbook ref={ref} {...settings} />
    </div>
  );
}
